import { ActionIcon, Avatar, Badge, Group, Menu, Text, Tooltip } from '@mantine/core';
import { notify } from '@/lib/notify';
import { IconDotsVertical, IconMailForward } from '@tabler/icons-react';
import { useCallback, useMemo, useState } from 'react';
import { UnifyTable, UnifyTableSortAffordance, type UnifyTableColumn } from '@/components/UnifyTable';
import { PractitionerNameText } from '@/components/PractitionerNameText';
import { formatUsDateTime } from '@/lib/dateHelpers';
import { User, ROLE_LABELS, heldRolesForUser } from '../types/rbac';
import { formatCollapsedRoleLabels } from '@unify/fhir-model/role-display';

export type UserSortColumn = 'name' | 'email' | 'lastActive';

interface UserTableProps {
  users: User[];
  onUpdateUser: (id: string, updates: Partial<User>) => void;
  onViewUser?: (user: User) => void;
  onResendInvite?: (email: string) => Promise<void>;
  sortColumn: UserSortColumn;
  sortDirection: 'asc' | 'desc';
  onSort: (column: UserSortColumn) => void;
  /** When true, omit the Role column (e.g. Clients tab). */
  hideRoles?: boolean;
}

function getStatusColor(status: string) {
  if (status === 'Active') return 'green';
  if (status === 'Pending') return 'yellow';
  return 'red';
}

/** Display directory last-active. Prefer FHIR instants; keep legacy wall-clock mock strings. */
export function formatLastActive(dateStr: string): string {
  if (!dateStr || dateStr === '—') return '—';
  // Legacy mock / pre-fix values (no timezone) — show as authored.
  if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}$/.test(dateStr)) return dateStr;
  return formatUsDateTime(dateStr) || '—';
}

/** Sort key for lastActive (ISO instant, legacy wall-clock, or em dash). */
export function lastActiveSortKey(dateStr: string): number {
  if (!dateStr || dateStr === '—') return Number.NEGATIVE_INFINITY;
  if (/^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}$/.test(dateStr)) {
    const t = Date.parse(dateStr.replace(' ', 'T'));
    return Number.isNaN(t) ? Number.NEGATIVE_INFINITY : t;
  }
  const t = Date.parse(dateStr);
  return Number.isNaN(t) ? Number.NEGATIVE_INFINITY : t;
}

// UnifyTable rows activate on Enter/Space; keep row actions from also triggering navigation.
function stopRowActivation(e: React.KeyboardEvent) {
  if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
}

function canResendInvite(user: User): boolean {
  return user.status === 'Pending' && user.email.trim().length > 0;
}

function UserRowActions({
  user,
  onResendInvite,
}: {
  user: User;
  onResendInvite: (email: string) => Promise<void>;
}) {
  const [sending, setSending] = useState(false);

  const handleResend = useCallback(async () => {
    setSending(true);
    try {
      await onResendInvite(user.email);
      notify({
        title: 'Invite sent',
        message: `Invitation resent to ${user.email}`,
        type: 'success',
      });
    } catch {
      notify({
        title: 'Failed',
        message: 'Could not resend invite. Try again later.',
        type: 'error',
      });
    } finally {
      setSending(false);
    }
  }, [onResendInvite, user.email]);

  if (!canResendInvite(user)) return null;

  return (
    <Menu shadow="md" width={220} position="bottom-end">
      <Menu.Target>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          aria-label="Row actions"
          loading={sending}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={stopRowActivation}
        >
          <IconDotsVertical size={16} />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown onClick={(e) => e.stopPropagation()} onKeyDown={stopRowActivation}>
        <Menu.Item
          leftSection={<IconMailForward size={16} />}
          disabled={sending}
          onClick={() => {
            void handleResend();
          }}
        >
          Resend email invite
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}

export function UserTable({
  users,
  onUpdateUser,
  onViewUser,
  onResendInvite,
  sortColumn,
  sortDirection,
  onSort,
  hideRoles = false,
}: UserTableProps) {
  void onUpdateUser;

  const columns = useMemo((): UnifyTableColumn<User>[] => {
    const sortableHeader = (col: UserSortColumn, label: string) => {
      const active = sortColumn === col;
      return (
        <Group
          gap={6}
          wrap="nowrap"
          onClick={() => onSort(col)}
          style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
        >
          <Text component="span" size="sm" fw={600}>
            {label}
          </Text>
          <UnifyTableSortAffordance active={active} direction={sortDirection} />
        </Group>
      );
    };

    const base: UnifyTableColumn<User>[] = [
      {
        key: 'name',
        label: 'Name',
        renderHeader: () => sortableHeader('name', 'Name'),
        render: (user) => (
          <Group gap="sm" wrap="nowrap" maw="100%">
            <Avatar radius="xl" size="sm" color="gold" style={{ flexShrink: 0 }}>
              {user.name
                .split(' ')
                .filter((n) => n && !n.endsWith('.'))
                .map((n) => n[0])
                .join('')
                .toUpperCase()}
            </Avatar>
            <Tooltip
              label={user.legalName ? `${user.name} (${user.legalName})` : user.name}
              disabled={!user.legalName}
            >
              <PractitionerNameText
                size="sm"
                fw={500}
                truncate
                primary={user.name}
                legal={user.legalName}
              />
            </Tooltip>
          </Group>
        ),
        getValue: (u) => u.name,
      },
      {
        key: 'email',
        label: 'Email',
        renderHeader: () => sortableHeader('email', 'Email'),
        render: (user) => (
          <Text size="sm" c="gray.6" truncate title={user.email}>
            {user.email}
          </Text>
        ),
        getValue: (u) => u.email,
      },
      {
        key: 'status',
        label: 'Status',
        width: 110,
        render: (user) => (
          <Badge color={getStatusColor(user.status)} variant="light" size="sm">
            {user.status}
          </Badge>
        ),
        getValue: (u) => u.status,
      },
      {
        key: 'lastActive',
        label: 'Last Active',
        width: 140,
        renderHeader: () => sortableHeader('lastActive', 'Last Active'),
        render: (user) => (
          <Text size="sm" c="gray.6" style={{ whiteSpace: 'nowrap' }}>
            {formatLastActive(user.lastActive)}
          </Text>
        ),
        getValue: (u) => formatLastActive(u.lastActive),
      },
    ];

    if (!hideRoles) {
      base.splice(2, 0, {
        key: 'role',
        label: 'Role',
        render: (user) => {
          const held = heldRolesForUser(user);
          const label =
            formatCollapsedRoleLabels(held)
            || (held.length === 0 ? '—' : ROLE_LABELS[user.role] || user.role);
          return (
            <Text size="sm" truncate title={label}>
              {label}
            </Text>
          );
        },
        getValue: (u) => {
          const held = heldRolesForUser(u);
          return formatCollapsedRoleLabels(held) || (held.length === 0 ? '—' : ROLE_LABELS[u.role] || u.role);
        },
      });
    }

    if (onResendInvite && users.some(canResendInvite)) {
      base.push({
        key: 'actions',
        label: '',
        render: (user) => <UserRowActions user={user} onResendInvite={onResendInvite} />,
        getValue: () => '',
      });
    }

    return base;
  }, [hideRoles, onResendInvite, onSort, sortColumn, sortDirection, users]);

  return (
    <UnifyTable
      withPaper={false}
      columns={columns}
      data={users}
      getRowKey={(u) => u.id}
      pagination
      defaultRowsPerPage={25}
      onRowClick={onViewUser}
    />
  );
}
